(ns hai.tasks.sync-mark
  "The save mark in an open task's header, as the prototype shows it. It says
   how the task's writes are going — a history clock at rest, dots pulsing
   while you type and until what you typed is written, a tick for a second
   once a write lands — and a press opens the task's history: each change, who
   made it and when, newest first. Refresh, in the popover, sends what is typed now rather
   than after its pause, then fetches the task and its history as they now are
   ([[hai.tasks.data/::refresh]])."
  (:require
   [garden.stylesheet]
   [hai.tasks.assignee-bubble :as bubble]
   [hai.tasks.data :as data]
   [hai.tasks.history :as history]
   [hai.ui.components :as c]
   [hai.ui.components.datetime :as datetime]
   [hai.ui.components.tooltip :as tt]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private typing-icon (icons/mui-icon :outlined :more_horiz))
(def ^:private done-icon (icons/mui-icon :outlined :check))
(def ^:private refresh-icon (icons/mui-icon :outlined :sync))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules sync-mark-keyframes
  (garden.stylesheet/at-keyframes
   :sync-mark-spin
   ["100%" {:transform "rotate(360deg)"}])
  (garden.stylesheet/at-keyframes
   :sync-mark-pulse
   ["0%" {:opacity "1"}]
   ["50%" {:opacity "0.35"}]
   ["100%" {:opacity "1"}]))

;; Three faces stacked in one button, and only their opacity moves: a change of
;; face reads as a change, not a flicker. The host's class gives the button its
;; shape and colour. Applied by name: a Radix trigger needs a plain :button it
;; can put a ref on.
(o/defstyled mark-button :button
  {:position "relative"}
  [:.save-face
   {:position "absolute" :inset "0"
    :display "flex" :align-items "center" :justify-content "center"
    :opacity "0"
    :transition "opacity 0.22s ease"}
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]]
  ;; A write that typing didn't start leaves the clock up.
  [#{:&.idle :&.syncing} [:.save-face-idle {:opacity "1"}]]
  [:&.pending
   [:.save-face-pending {:opacity "1" :color t/--card-warning-text}
    [:svg {:animation "sync-mark-pulse 1.1s ease-in-out infinite"}]]]
  [:&.done [:.save-face-done {:opacity "1" :color t/--task-accent}]]
  ;; Held lit while its popover is open. Class and attribute both, to outweigh
  ;; the host's own rule for the button at rest.
  ["&.open[data-state='open']" {:background t/--note-kraft-10}])

(o/defstyled history-popover :div
  {:width "272px"}
  [:.history-head
   {:display "flex" :align-items "center" :gap "4px"
    :margin-bottom "8px"}]
  [:.history-title
   {:flex "1" :min-width "0"
    :display "flex" :align-items "center" :gap "6px"
    :font-size "13px" :font-weight "800"
    :color t/--card-forest}
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]]
  [:.history-action
   {:flex "none"
    :width "26px" :height "26px" :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "7px"
    :background "transparent"
    :color t/--card-forest-70
    :cursor "pointer"}
   [:&:hover {:background t/--card-forest-06 :color t/--card-forest}]
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]
   [:&.turning [:svg {:animation "sync-mark-spin 0.9s linear infinite"}]]]
  [:.history-changes
   {:display "flex" :flex-direction "column" :gap "9px"
    :max-height "264px"
    :overflow-y "auto"
    :scrollbar-width "thin"
    :scrollbar-color "var(--note-sage-55) var(--card-forest-10)"}]
  [:.history-quiet
   {:padding "2px"
    :font-size "12.5px"
    :color t/--link-muted}])

(o/defstyled change-row :div
  {:display "flex" :align-items "flex-start" :gap "9px"
   :min-width "0"}
  [:.change-text
   {:flex "1" :min-width "0"
    :display "flex" :flex-direction "column" :gap "1px"}]
  [:.change-what
   {:font-size "13px" :font-weight "700" :line-height "1.3"
    :color t/--card-forest}]
  [:.change-who
   {:font-size "11.5px" :line-height "1.3"
    :color t/--link-muted
    :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}])

(defn- assignee-words
  "Whom a change of assignee gave the task to, as its line says it: \"you\",
   their name, or nil for nobody."
  [assigned-to current-user]
  (cond
    (nil? assigned-to) nil
    (data/assignee-me? current-user assigned-to) "you"
    :else (or (data/assignee-name assigned-to) "someone")))

(defn- change-line
  "One change: whoever made it as their circle, what it did, and under that
   their name and when. A change nobody is named for has an empty circle and
   the time alone."
  [{:keys [change current-user]}]
  (let [{:task.change/keys [at by parts assigned-from assigned-to]} change
        me? (and (some? by) (data/assignee-me? current-user by))
        who (cond
              me? "You"
              by (data/assignee-name by))
        made-at (datetime/day-time-label at)]
    [change-row
     [bubble/assignee-avatar {:assignee by :me? me?}]
     [:span.change-text
      [:span.change-what (history/change-text parts
                                              {:to (assignee-words assigned-to current-user)
                                               :reassigned? (some? assigned-from)})]
      [:span.change-who (if who
                          (str who " · " made-at)
                          made-at)]]]))

(defn- history-body
  "The popover's inside: its title over the task's changes, with Refresh and
   Close in its corner. Refresh turns only for its own press, until the task
   and its history are back: opening the popover fetches the history too, and
   is no refresh."
  [{:keys [task-uuid change-fx on-close]}]
  (let [{:keys [changes loading? failed? refreshing?]} @(<s [::data/history task-uuid])
        current-user @(<s [:auth/identity])]
    [history-popover
     [:div.history-head
      [:span.history-title
       [icons/history]
       [:span "History"]]
      [tt/tooltip {:content "Refresh"}
       [:button.history-action
        {:type "button"
         :class (when refreshing? "turning")
         :aria-label "Refresh"
         :on-click #(>e [::data/refresh task-uuid change-fx])}
        [refresh-icon]]]
      [tt/tooltip {:content "Close"}
       [:button.history-action
        {:type "button"
         :aria-label "Close"
         :on-click on-close}
        [icons/outlined-close]]]]
     (cond
       (seq changes)
       (into [:div.history-changes]
             (map-indexed (fn [index change]
                            ^{:key index}
                            [change-line {:change change
                                          :current-user current-user}]))
             changes)

       loading? [:div.history-quiet "Loading…"]
       failed? [:div.history-quiet "Couldn't load the history."]
       :else [:div.history-quiet "Nothing yet."])]))

(defn mark
  "The save mark for `task`, for its header; `class` gives it the host's button
   look (`bar-action-btn` in [[hai.tasks.header/bar]]), and `change-fx`, the
   host's re-read of the task, is what Refresh fetches with."
  [{:keys [task class change-fx]}]
  (r/with-let [!open? (r/atom false)]
    (let [task-uuid (:task/uuid task)
          sync @(<s [::data/sync task-uuid])
          open? @!open?]
      [c/PopoverRoot {:open open?
                      :onOpenChange (fn [opening?]
                                      (when opening?
                                        (>e [::data/load-history task-uuid]))
                                      (reset! !open? opening?))}
       ;; Named on hover in the house tooltip, as every header button is.
       [tt/tooltip {:content "History"}
        [c/PopoverTrigger
         [:button {:type "button"
                   :class [(str mark-button) class (name sync) (when open? "open")]
                   :aria-label "History"
                   ;; Pressed mid-sentence, the field keeps the focus and stays
                   ;; open; a blur would close it.
                   :on-mouse-down #(.preventDefault %)}
          [:span.save-face.save-face-idle [icons/history]]
          [:span.save-face.save-face-pending [typing-icon]]
          [:span.save-face.save-face-done [done-icon]]]]]
       [c/PopoverContent {:side "bottom" :align "end" :sideOffset 6 :size "1"
                          :onOpenAutoFocus #(.preventDefault %)
                          :onCloseAutoFocus #(.preventDefault %)}
        [history-body {:task-uuid task-uuid
                       :change-fx change-fx
                       :on-close #(reset! !open? false)}]]])))
